import { useState, useEffect } from 'react';
import {
  Phone, Mail, MapPin,
  FileText, Check,
  Calendar
} from 'lucide-react';

// Composants partagés
import SectionTitle from './components/shared/SectionTitle';
import AnimatedSection from './components/shared/AnimatedSection';

// shadcn components
import TestimonialsModern from './components/ui/testimonials-modern';
import { HowToUse } from './components/ui/how-to-use';
import { AnimatedHero } from './components/ui/animated-hero';
import ServicesSection from './components/ui/services';
import { BelgiumMapDemo } from './components/ui/belgium-map-demo';
import { EsiteckCardNav } from './components/ui/esiteck-card-nav';
import { AdvantagesOptimized } from './components/ui/advantages-optimized';
import { ContactCard } from './components/ui/contact-card';
import { Input } from './components/ui/input';
import { Label } from './components/ui/label';
import { Textarea } from './components/ui/textarea';
import { Footer } from './components/ui/footer';
import { EmergencySectionModern } from './components/ui/emergency-section-modern';
import BrandPartnersDemo from './components/ui/brand-partners-demo';
import GradientButton from './components/ui/button-1';

// Hooks et utilitaires
import { debounce } from './utils/debounce';

const EsiteckLanding = () => {
  const [scrolled, setScrolled] = useState(false);
  const [formData, setFormData] = useState({
    nom: '',
    telephone: '',
    email: '',
    service: '',
    message: ''
  });
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Scroll handler avec debounce
  useEffect(() => {
    const handleScroll = debounce(() => {
      setScrolled(window.scrollY > 20);
    }, 10);

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleFormChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleFormSubmit = async (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    
    // Basic form validation
    if (!formData.nom || !formData.email || !formData.telephone || !formData.service) {
      alert('Veuillez remplir tous les champs obligatoires.');
      return;
    }
    
    setIsSubmitting(true);
    
    // Simulation d'envoi
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    setFormSubmitted(true);
    setIsSubmitting(false);
    setTimeout(() => setFormSubmitted(false), 3000);
    setFormData({ nom: '', telephone: '', email: '', service: '', message: '' });
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Skip to content pour accessibilité */}
      <a href="#main" className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 bg-blue-900 text-white px-4 py-2 rounded-lg z-50">
        Aller au contenu principal
      </a>

      {/* Modern Card Navigation */}
      <EsiteckCardNav scrollToSection={scrollToSection} />

      <main id="main">
        {/* Modern Animated Hero Section */}
        <div className="pt-24">
          <AnimatedHero scrollToSection={scrollToSection} />
        </div>

        {/* Brand Partners Section */}
        <BrandPartnersDemo />

        {/* Modern Services Section */}
        <div id="services">
          <ServicesSection />
        </div>

        {/* Optimized Advantages Section */}
        <AdvantagesOptimized />

        {/* Modern Belgium Map Section */}
        <div id="zone-intervention">
          <BelgiumMapDemo />
        </div>

        {/* Modern How To Use Section */}
        <AnimatedSection id="processus" className="bg-gray-50 py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionTitle
              title="Comment Ça Marche ?"
              subtitle="Un processus simple et efficace en 4 étapes"
            />
            <div className="mt-16">
              <HowToUse />
            </div>
          </div>
        </AnimatedSection>

        {/* Modern Testimonials Section */}
        <div id="temoignages">
          <TestimonialsModern />
        </div>

        {/* Modern Emergency Section */}
        <EmergencySectionModern />

        {/* Modern Contact Section */}
        <section id="contact" className="bg-gray-50 py-24">
          <div className="w-full px-4 sm:px-6 lg:px-8">
            <ContactCard
              title="Demandez Votre Devis Gratuit"
              description="Remplissez ce formulaire et nous vous recontactons dans les plus brefs délais pour tous vos besoins en plomberie, chauffage et dépannage."
              contactInfo={[
                {
                  icon: Phone,
                  label: 'Téléphone',
                  value: '0465 19 57 26',
                },
                {
                  icon: Mail,
                  label: 'Email',
                  value: 'contact@esiteck.com',
                },
                {
                  icon: MapPin,
                  label: 'Adresse',
                  value: 'Avenue Louise 123, Bruxelles',
                },
                {
                  icon: Calendar,
                  label: 'Disponibilité',
                  value: '24h/24 - 7 jours sur 7',
                },
              ]}
            >
              {formSubmitted ? (
                <div className="text-center py-16 w-full">
                  <div className="w-20 h-20 bg-gradient-to-br from-green-50 to-green-100 rounded-xl flex items-center justify-center mx-auto mb-8">
                    <Check className="w-10 h-10 text-green-600" />
                  </div>
                  <h3 className="text-3xl font-bold text-blue-900 mb-4">Merci !</h3>
                  <p className="text-xl text-gray-600">Nous vous contacterons très bientôt.</p>
                </div>
              ) : (
                <form className="w-full space-y-4">
                  <div className="flex flex-col gap-2">
                    <Label>Nom complet *</Label>
                    <Input 
                      type="text" 
                      name="nom"
                      value={formData.nom}
                      onChange={handleFormChange}
                      placeholder="Votre nom"
                      required
                    />
                  </div>
                  <div className="flex flex-col gap-2">
                    <Label>Email *</Label>
                    <Input 
                      type="email" 
                      name="email"
                      value={formData.email}
                      onChange={handleFormChange}
                      placeholder="votre@email.com"
                      required
                    />
                  </div>
                  <div className="flex flex-col gap-2">
                    <Label>Téléphone *</Label>
                    <Input 
                      type="tel" 
                      name="telephone"
                      value={formData.telephone}
                      onChange={handleFormChange}
                      placeholder="+32"
                      required
                    />
                  </div>
                  <div className="flex flex-col gap-2">
                    <Label>Service</Label>
                    <select 
                      name="service"
                      value={formData.service}
                      onChange={handleFormChange}
                      className="flex h-10 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                      required
                    >
                      <option value="">Sélectionnez un service</option>
                      <option value="plomberie">Plomberie</option>
                      <option value="chauffage">Chauffage</option>
                      <option value="debouchage">Débouchage</option>
                      <option value="serrurerie">Serrurerie</option>
                    </select>
                  </div>
                  <div className="flex flex-col gap-2">
                    <Label>Message</Label>
                    <Textarea 
                      name="message"
                      value={formData.message}
                      onChange={handleFormChange}
                      placeholder="Décrivez votre besoin..."
                    />
                  </div>
                  <GradientButton
                    variant="primary"
                    width="100%"
                    height="50px"
                    onClick={handleFormSubmit}
                    disabled={isSubmitting}
                    className="w-full"
                  >
                    {isSubmitting ? 'Envoi en cours...' : 'Envoyer la Demande'}
                  </GradientButton>
                </form>
              )}
            </ContactCard>
          </div>
        </section>
      </main>

      {/* Modern Footer */}
      <Footer />
    </div>
  );
};

export default EsiteckLanding;